<template>
    <teleport to="body">
        <transition
            enter-active-class="duration-200 ease-out"
            enter-from-class="opacity-0"
            enter-to-class="opacity-100"
            leave-active-class="duration-150 ease-in"
            leave-from-class="opacity-100"
            leave-to-class="opacity-0"
        >
            <div
                v-if="show"
                class="fixed inset-0 z-[2147483647] flex items-start justify-center bg-black/50 p-4 pt-[14vh]"
                role="dialog"
                aria-modal="true"
                aria-labelledby="shortcuts-overview-title"
                @click.self="emit('close')"
            >
                <div
                    ref="panelEl"
                    class="w-full max-w-2xl rounded-lg bg-white dark:bg-neutral-900 border border-neutral-200 dark:border-neutral-700 shadow-xl overflow-hidden"
                    @keydown="onKeydown"
                >
                    <div
                        class="flex items-center justify-between px-5 py-3 border-b border-neutral-200 dark:border-neutral-800"
                    >
                        <h3
                            id="shortcuts-overview-title"
                            class="text-sm font-semibold text-neutral-700 dark:text-neutral-200"
                        >
                            {{ heading }}
                        </h3>
                        <button
                            ref="closeEl"
                            type="button"
                            class="text-neutral-400 hover:text-neutral-600 dark:hover:text-neutral-200 focus:outline-none"
                            :aria-label="closeLabel"
                            @click="emit('close')"
                        >
                            <Icon icon="phosphor/regular/x" class="size-4" />
                        </button>
                    </div>

                    <div
                        class="px-5 py-4 max-h-[70vh] overflow-y-auto scrollbar-float grid gap-5 grid-cols-[repeat(auto-fit,minmax(14rem,1fr))]"
                    >
                        <section v-for="group in groups" :key="group.title">
                            <h4
                                class="text-xs font-semibold uppercase tracking-wide text-neutral-400 dark:text-neutral-500 mb-2"
                            >
                                {{ group.title }}
                            </h4>
                            <ul class="space-y-1.5">
                                <li
                                    v-for="row in group.rows"
                                    :key="row.label"
                                    class="flex items-center justify-between gap-4 text-sm"
                                >
                                    <span class="text-neutral-600 dark:text-neutral-300">{{ row.label }}</span>
                                    <span class="inline-flex items-center gap-1 shrink-0">
                                        <KbdShortcut v-for="combo in row.keys" :key="combo" :keys="combo" size="sm" />
                                    </span>
                                </li>
                            </ul>
                        </section>
                    </div>
                </div>
            </div>
        </transition>
    </teleport>
</template>

<script setup lang="ts">
import { nextTick, ref, watch } from 'vue';
import KbdShortcut from '@src/Common/Components/KbdShortcut.vue';
import { createFocusTrap } from '@src/Common/Builder/focusTrap';

export interface ShortcutsGroup {
    title: string;
    rows: Array<{ keys: string[]; label: string }>;
}

const props = withDefaults(
    defineProps<{
        show: boolean;
        groups: ShortcutsGroup[];
        heading?: string;
        closeLabel?: string;
    }>(),
    {
        heading: 'Klávesové zkratky',
        closeLabel: 'Zavřít',
    },
);

const emit = defineEmits<{ close: [] }>();

const panelEl = ref<HTMLElement | null>(null);
const closeEl = ref<HTMLButtonElement | null>(null);
const trap = createFocusTrap();

watch(
    () => props.show,
    async (value) => {
        if (value) {
            await nextTick();

            if (panelEl.value) {
                trap.activate(panelEl.value);
            }

            closeEl.value?.focus();
        } else {
            trap.deactivate();
        }
    },
);

const onKeydown = (event: KeyboardEvent): void => {
    if (event.key === 'Escape') {
        event.preventDefault();
        emit('close');
        return;
    }

    trap.onKeydown(event);
};
</script>
